<template>
    <div class="learning-admin-path-list" data-test-view="learning-admin-path">
        <l-headline
            :title="$t(`learning.admin.path.title`)"
            :sub-title="$t(`learning.admin.path.subTitle`)"
            :tabs="tabs"
        ></l-headline>

        <div v-show="hasElements">
            <div class="row filters">
                <div :class="[`col-md-${12 - 2 * filters.length}`]">
                    <l-input-text
                        v-model="query"
                        type="text"
                        :optional="true"
                        :label="''"
                        :placeholder="$t(`learning.admin.path.search.placeholder`)"
                        data-test-id="learning-admin-path-search"
                    />
                </div>
                <div v-for="filter in filters" :key="filter.key" class="col-md-2">
                    <l-select
                        :data-test-id="`learning-admin-path-filter-${filter.key}`"
                        :options="
                            filter.options.map(option => ({
                                value: option.value,
                                text:
                                    option.text ||
                                    $t(`learning.admin.path.filters.${filter.key}.${option.label}`),
                            }))
                        "
                        :value="filter.value"
                        @change="
                            e => {
                                filter.value = e.target.value;

                                filterBy(filter.key, filter.value);
                            }
                        "
                    />
                </div>
            </div>

            <hr />
        </div>

        <l-paginate
            :list="filteredList"
            :items-per-page="10"
            :on-item-click="goToDashboardPath"
            :no-data-text="
                hasElements ? $t(`learning.admin.path.noResults`) : $t(`learning.admin.path.empty`)
            "
            :no-data-btn-show="rights.L_PATH"
            :no-data-btn-text="$t(`learning.admin.path.btn.createEmpty`)"
            :no-data-btn-handler="createNewPath"
        >
            <template #item="{ item }">
                <l-list-item class="clickable" data-test-class="path-list-item">
                    <template #title>
                        <span class="path-name ellipsis" data-test-id="path-name">{{
                            item.name
                        }}</span>
                    </template>

                    <template #subtitle>
                        <div v-if="item.description" class="path-description ellipsis">
                            <i class="fal fa-comment"></i>
                            {{ item.description }}
                        </div>
                        <div class="path-info">
                            <span class="path-info-status">
                                <i :class="['fa', `fa-${getIconFromStatus(item.status)}`]"></i
                                >{{ $t(`learning.admin.path.details.status.label.${item.status}`) }}
                                {{
                                    $tc(
                                        `learning.admin.path.details.steps.count`,
                                        item.steps.length,
                                        {
                                            count: item.steps.length,
                                        },
                                    )
                                }}
                                <span v-if="getTargetingLabel(item)">
                                    {{
                                        $tc(
                                            `learning.admin.path.details.steps.labelTargeting`,
                                            item.steps.length,
                                            {
                                                target: getTargetingLabel(item),
                                            },
                                        )
                                    }}
                                </span>
                            </span>
                        </div>
                    </template>
                </l-list-item>
            </template>
        </l-paginate>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import ObjectId from "bson-objectid";

import LHeadline from "@/components/LHeadline";
import LInputText from "@/components/LInputText";
import LListItem from "@/components/LListItem";
import LPaginate from "@/components/LPaginate";
import LSelect from "@/components/LSelect";

export default {
    components: {
        LHeadline,
        LInputText,
        LListItem,
        LPaginate,
        LSelect,
    },
    data() {
        return {
            query: "",
        };
    },
    computed: {
        ...mapGetters({
            rights: "users/current/rights",
            search: "learning/path/search",
            list: "learning/path/filteredList",
            filters: "learning/path/filters",
            hasElements: "learning/path/hasElements",
        }),
        tabs() {
            return [
                {
                    tabClass: "space",
                    show: true,
                },
                {
                    show: this.rights.L_PATH,
                    label: this.$t(`learning.admin.path.btn.create`),
                    handler: this.createNewPath,
                    testId: "learning-admin-path-create",
                    tabClass: "btn-primary",
                    isButton: true,
                },
            ];
        },
    },
    watch: {
        list: {
            immediate: true,
            handler() {
                this.filteredList = this.search(this.query);
            },
        },
        query: {
            immediate: true,
            handler(newQuery) {
                if (newQuery.length) {
                    this.filteredList = this.search(newQuery);
                } else {
                    this.filteredList = this.list;
                }
            },
        },
    },
    methods: {
        createNewPath() {
            const id = ObjectId().toString();

            this.goToEditPath({ commonId: id });
        },
        goToEditPath({ commonId }) {
            this.$router.push(`/learning/admin/path/${commonId}/edit`);
        },
        goToDashboardPath({ commonId }) {
            this.$router.push(`/learning/admin/path/${commonId}/dashboard`);
        },
        getTargetingLabel(item) {
            return item.access.targeting.all.enabled
                ? this.$t(`learning.admin.path.details.targeting.participants.label.all`)
                : [
                      ...item.access.targeting.teams.include.map(team => team.name),
                      ...item.access.targeting.users.include.map(user => user.fullname),
                  ]
                      .reduce((a, c) => a + c + ", ", "")
                      .slice(0, -2);
        },
        getIconFromStatus(status) {
            switch (status) {
                case "draft":
                    return "pencil";
                case "active":
                    return "calendar-check-o";
                case "removed":
                    return "cross";
                default:
                    return "pencil";
            }
        },
        filterBy(key, value) {
            this.$store.dispatch({ type: "learning/path/filterBy", payload: { key, value } });
        },
    },
};
</script>

<style scoped>
.dropdown {
    margin-top: 0px;
}

.filters {
    margin-bottom: 20px;
}
.filters >>> .form-group {
    padding-bottom: 7px;
}

.learning-admin-path-list >>> .list-item-right {
    width: 100%;
}

.path-description i {
    margin-right: 8px;
}

.path-info {
    display: flex;
}
.path-info span {
    margin-right: 8px;
}
.path-info i {
    margin-right: 10px;
}
.path-info-targeting {
    width: 80%;
    display: inline-block !important;
}

@media (max-width: 600px) {
    .path-info {
        display: block;
    }
    .path-info-targeting {
        width: 100%;
    }
}
</style>
